import React, { useContext, useEffect, useState } from 'react';
import { initializeNewRelicTrackingContainer } from '@vp/om-newrelic-tracker';
import PropTypes from 'prop-types';
import { H1 } from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { BasicPreloader } from '../shared/BasicPreloader';
import useIdentityContext from '../../hooks/useIdentityContext';
import useSiteData from '../../hooks/useSiteData';
import { InternalViewOrderDetailsSession } from './internalView/InternalViewOrderDetailsSession';
import { OrderDetailsApplication } from './OrderDetailsApplication';
import { AnonymousOrderDetailsApplication } from './anonymousOrders/AnonymousOrderDetailsApplication';
import { InternalViewProvider } from '../contexts/InternalViewContext';
import { SourcePageContext } from '../contexts/SourcePageContext';

/* This visually hidden heading is intended for those using assistive tech because assistive tech expects the H1 to be at the top of the page.
 * This heading is hidden from visual users to prevent redundancy since we also render a visible heading lower in the component tree */
const VisuallyHiddenH1 = ({ isConfirmationPage }) => {
  const [orderDetailsMessages, orderConfirmationMessages] = useTranslations([
    'orderDetailsMessages',
    'orderConfirmationMessages',
  ]);

  const [headingText, setHeadingText] = useState(
    orderDetailsMessages.titleMessage
  );

  useEffect(() => {
    if (isConfirmationPage) {
      setHeadingText(
        <ReplaceTokens
          tokenizedString={orderConfirmationMessages.titleMessage}
          substitutions={{
            lineBreak: <br />,
          }}
        />
      );
    }
  }, [isConfirmationPage, orderConfirmationMessages.titleMessage]);

  return (
    <H1 visuallyHidden data-testid="hidden-order-details-heading">
      {headingText}
    </H1>
  );
};

VisuallyHiddenH1.propTypes = {
  isConfirmationPage: PropTypes.bool,
};

/*

- Anonymous users using a tenant that does NOT support anonymous sessions will be prompted to sign-in (auth)

- The order confirmation page is only accessible by logged-in users AND anonymous users
in the same session of which the order was placed (since their anonymousID will match the ID on the fetched order)

- All users are directed to the order lookup form if no order ID is provided as query parameter in URL path

*/

export const OrderDetailsSession = ({ orderId, isGuest }) => {
  const {
    allowAnonymousSessions,
    externalAppNavigation: nav,
    locale,
    siteName,
    vistacartTenant: tenant,
  } = useSiteData();
  const { isIdentityInitialized, identity, auth } = useIdentityContext();
  const {
    isShopper,
    isInternalUser,
    isAnonymousUser,
    shopperId,
    anonymousUserId,
  } = identity;
  const { isConfirmationPage } = useContext(SourcePageContext);

  const orderIdToDisplay = isGuest ? orderId : undefined;

  useEffect(() => {
    initializeNewRelicTrackingContainer({
      // omitting actionRoot to keep parity with current events
      // actionRoot: 'om-orders-ui',
      shopperId: shopperId || anonymousUserId,
      locale,
      site: siteName,
      tenant,
    });
  }, [anonymousUserId, locale, shopperId, siteName, tenant]);

  // Session Identity unknown, display BasicPreloader as auth does its thing.
  if (!isIdentityInitialized) {
    return (
      <>
        <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
        <BasicPreloader isCentered size="super" mt="xl" mb="xl" />
      </>
    );
  }

  if (isInternalUser) {
    return (
      <>
        <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
        <InternalViewProvider>
          <InternalViewOrderDetailsSession
            orderId={orderId}
            confirmationMode={isConfirmationPage}
          />
        </InternalViewProvider>
      </>
    );
  }

  if (isAnonymousUser) {
    if (allowAnonymousSessions) {
      if (isConfirmationPage) {
        return (
          <>
            <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
            <OrderDetailsApplication
              shopperId={anonymousUserId}
              orderId={orderId}
            />
          </>
        );
      }

      return (
        <>
          <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
          <AnonymousOrderDetailsApplication orderIdOnPath={orderIdToDisplay} />
        </>
      );
    }
    auth.signIn();
    return (
      <>
        <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
        <BasicPreloader isCentered size="super" mt="xl" mb="xl" />
      </>
    );
  }

  if (isShopper) {
    if (!orderId) {
      // navigate to Order History if shopper signed in with no order number
      const orderHistoryUrl = nav.orderHistory;
      // adding null check in case site doesn't have nav.orderHistory defined
      if (orderHistoryUrl) {
        window.location.assign(`${orderHistoryUrl}`);
        // stop the code from moving on
        return false;
      }
    }

    return (
      <>
        <VisuallyHiddenH1 isConfirmationPage={isConfirmationPage} />
        <OrderDetailsApplication
          shopperId={shopperId}
          orderId={orderId}
          confirmationMode={isConfirmationPage}
        />
      </>
    );
  }

  console.warn(
    'Order Details Session Identity Status Unknown. No Order Details Application will be rendered.'
  );
  return null;
};

OrderDetailsSession.propTypes = {
  orderId: PropTypes.string,
  isGuest: PropTypes.bool,
};
